import type { VoidFunctionComponent } from 'react';
import { useEffect, useState } from 'react';
import * as _ from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import * as qs from 'qs';

import { Menu, MenuDivider, MenuGroup, MenuHeader, MenuItem, SubMenu } from '../../generic/Menu';
import Button from '../../generic/Button';
import { Icon, icons, IconText, RouterLink } from '../../generic';
import { userHasPermissionForTeamProjects, userHasPermissionToCreateReleaseGroups, userHasPermissionToEditAnyReleaseGroup } from '../../../helpers/permissions';
import { isProjects } from '../../../../shared/projectsV2';
import { Action, ResourceType } from '../../../../typings/permissions';
import { useAddToast } from '../../../contexts/ToastContext';
import { useOnMount } from '../../../hooks';
import * as ProjectsV2Store from '../../../redux/projectsV2';
import TeamsModule from '../../../redux/teams';
import PolicyStore from '../../../redux/policies';
import OrganizationStore from '../../../redux/organizations';
import utils from '../../../utils';
import useFetchProjectsAction from './hooks/useFetchProjectsAction';
import { DeleteProjectsModal, DeleteReleaseGroupsModal, RemoveTeamModal } from './modals';
import { window } from '../../../globals';
import { useUserPermittedPolicyTypes } from '../../shared/policies/hooks';
import useShowReleaseGroupLimitModal from './hooks/useShowReleaseGroupLimitModal';
import type { Team } from '../../../types';

const ACTION_REMOVE = -1;
const ACTION_MANAGE = -2;

type CreateReleaseGroupItemProps = {
  isAtOrOverLimit: boolean;
  createReleaseGroupUrl: string;
};

const CreateReleaseGroupItem: VoidFunctionComponent<CreateReleaseGroupItemProps> = ({
  isAtOrOverLimit,
  createReleaseGroupUrl,
}) => {
  const showReleaseGroupLimitModal = useShowReleaseGroupLimitModal();

  if (isAtOrOverLimit) {
    return (
      <MenuItem
        onClick={showReleaseGroupLimitModal}
      >
        Create release group...
      </MenuItem>
    );
  }

  return (
    <MenuItem>
      <RouterLink className="szh-menu__item--anchor" to={createReleaseGroupUrl}>
        Create release group...
      </RouterLink>
    </MenuItem>
  );
};

type Props = {
  shouldRestrictCreateReleaseGroup: boolean;
};

const ProjectActionsMenu: VoidFunctionComponent<Props> = ({ shouldRestrictCreateReleaseGroup }) => {
  // Local State
  const [canManageTeam, setCanManageTeam] = useState(false);
  const [availableTeams, setAvailableTeams] = useState<Team[]>([]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isRemoveTeamModalOpen, setIsRemoveTeamModalOpen] = useState(false);

  // Redux State
  const dispatch = useDispatch();
  const teams = useSelector(TeamsModule.selectors.getTeamList);
  const category = useSelector(ProjectsV2Store.selectors.getCategory);
  const areProjectSelected = useSelector(ProjectsV2Store.selectors.getAreSomeProjectsSelected);
  const selectedProjects = useSelector(ProjectsV2Store.selectors.getSelectedProjects);
  const selectedProjectsCount = useSelector(ProjectsV2Store.selectors.getSelectedProjectCount);
  const selectedProjectLocators = useSelector(ProjectsV2Store.selectors.getSelectedProjectLocators);
  const filters = useSelector(ProjectsV2Store.selectors.getProjectsFilterQuery);
  const labels = useSelector(ProjectsV2Store.selectors.getSortedOrganizationLabels);
  const groupedPolicies = useSelector(PolicyStore.selectors.getGroupedPolicies);
  const teamSelection = useSelector(TeamsModule.selectors.getTeamSelection);

  const isAssignPolicyDisabled = _.isEmpty(groupedPolicies);

  // only allow bulk project team removal when a single team is selected
  const canRemoveFromTeam =
    teamSelection !== 'null' &&
    teamSelection.length === 1 &&
    userHasPermissionForTeamProjects(Action.Remove, teamSelection[0] as number);
  const activeTeamName = teams.find((team) => team.id === Number(teamSelection[0]))?.name ?? 'the team';

  const addToast = useAddToast();
  const canEditAnyReleaseGroups = userHasPermissionToEditAnyReleaseGroup();
  const canCreateReleaseGroups = userHasPermissionToCreateReleaseGroups();
  const userPermittedPolicyTypes = useUserPermittedPolicyTypes();

  const createReleaseGroupUrl = `/projects/group/create?${qs.stringify({
    selected: selectedProjectLocators,
    ...filters,
  })}`;

  useOnMount(() => {
    setCanManageTeam(
      window.user?.teams.some((team) =>
        window.hasPermission({ action: Action.Edit, resourceType: ResourceType.Team, teamId: team.id }),
      ),
    );
    dispatch(OrganizationStore.actions.fetchOrgLabels());

    // Only try to fetch policies if the user has permission to view at least one policy type.
    if (!_.isEmpty(userPermittedPolicyTypes)) {
      dispatch(PolicyStore.actions.fetchAll());
    }
  });

  useEffect(() => {
    setAvailableTeams(teams?.filter((team) => userHasPermissionForTeamProjects(Action.Add, team.id)));
  }, [teams]);

  const handleShowDeleteModal = () => {
    setIsDeleteModalOpen(true);
  };

  const handleShowRemoveTeamModal = () => {
    setIsRemoveTeamModalOpen(true);
  };

  const addTeamToProject = useFetchProjectsAction(TeamsModule.actions.addTeamProjects);

  const onAddToTeam = async (teamId: number) => {
    await addTeamToProject(teamId, selectedProjectLocators, filters);
    dispatch(ProjectsV2Store.actions.clearSelection());
    addToast({ type: 'success', content: 'Successfully added project(s) to team.' });
  };

  const onAddLabelToProjects = useFetchProjectsAction(ProjectsV2Store.actions.bulkApplyLabel);

  const addLabelToProjects = async (labelId: number) => {
    await onAddLabelToProjects(labelId, addToast);
    dispatch(ProjectsV2Store.actions.clearSelection());
  };

  const onAssignPolicyToProjects = useFetchProjectsAction(ProjectsV2Store.actions.bulkApplyPolicy);

  const assignPolicyToProjects = async (policyId: number) => {
    await onAssignPolicyToProjects(policyId, addToast);
    dispatch(ProjectsV2Store.actions.clearSelection());
  };

  return (
    <>
      {isProjects(category) ? (
        <DeleteProjectsModal
          show={isDeleteModalOpen}
          selectedProjects={selectedProjects}
          selectedProjectsCount={selectedProjectsCount}
          onHide={() => setIsDeleteModalOpen(false)}
          onDelete={() => setIsDeleteModalOpen(false)}
        />
      ) : (
        <DeleteReleaseGroupsModal
          show={isDeleteModalOpen}
          selectedReleaseGroups={selectedProjects}
          selectedReleaseGroupsCount={selectedProjectsCount}
          onHide={() => setIsDeleteModalOpen(false)}
          onDelete={() => setIsDeleteModalOpen(false)}
        />
      )}
      <RemoveTeamModal
        show={isRemoveTeamModalOpen}
        selectedProjects={selectedProjects}
        selectedProjectsCount={selectedProjectsCount}
        onHide={() => setIsRemoveTeamModalOpen(false)}
        onDelete={() => setIsRemoveTeamModalOpen(false)}
        teamName={activeTeamName}
      />
      <Menu
        menuButton={
          <Button type="secondary" size="large" isDisabled={!areProjectSelected}>
            <div className="project-actions-menu">
              <span>Actions</span>
              <Icon icon={icons.caretDown} />
            </div>
          </Button>
        }
        boundingBoxPadding="100% 0"
        gap={4}
      >
        {isProjects(category) && (
          <SubMenu
            label="Add to team"
            disabled={!canRemoveFromTeam && !canManageTeam && !availableTeams?.length}
            menuClassName="team-nested-menu"
            setDownOverflow
            overflow="auto"
            position="auto"
            gap={8}
          >
            <MenuGroup takeOverflow>
              {availableTeams?.map((team) => (
                <MenuItem key={team.id} onClick={() => onAddToTeam(team.id)}>
                  <span style={{ color: utils.hashColor(team.name) }}>
                    <Icon icon={icons.square} />
                  </span>
                  {team.name}
                </MenuItem>
              ))}
              {canManageTeam && (
                <MenuItem key={ACTION_MANAGE}>
                  <RouterLink className="szh-menu__item--anchor" to="/account/settings/organization/teams">
                    <span>
                      <Icon icon={icons.cog} />
                      Manage Teams
                    </span>
                  </RouterLink>
                </MenuItem>
              )}
            </MenuGroup>
          </SubMenu>
        )}
        {canRemoveFromTeam && (
          <MenuItem key={ACTION_REMOVE} onClick={handleShowRemoveTeamModal}>
            Remove from team
          </MenuItem>
        )}
        {isProjects(category) && (
          <SubMenu
            label="Assign label"
            menuClassName="label-nested-menu"
            setDownOverflow
            overflow="auto"
            position="auto"
            align="center"
            gap={8}
          >
            <MenuGroup takeOverflow>
              {labels.map((label) => (
                <MenuItem
                  key={label.id}
                  onClick={() => {
                    addLabelToProjects(label.id);
                  }}
                >
                  {label.label}
                </MenuItem>
              ))}
            </MenuGroup>
          </SubMenu>
        )}
        <SubMenu
          label="Assign policy"
          setDownOverflow
          overflow="auto"
          position="auto"
          align="center"
          gap={8}
          disabled={isAssignPolicyDisabled}
        >
          <MenuGroup>
            {Object.entries(groupedPolicies).map(([type, policies], typeIdx) => (
              <>
                <MenuHeader>{type}</MenuHeader>
                {policies.map((policy) => (
                  <MenuItem
                    onClick={() => {
                      assignPolicyToProjects(policy.id);
                    }}
                  >
                    {policy.title}
                  </MenuItem>
                ))}
                {/* Don't put a divider on the last one since we put one right after outside the MenuGroup */}
                {typeIdx !== Object.keys(groupedPolicies).length - 1 && <MenuDivider />}
              </>
            ))}
          </MenuGroup>
          <MenuDivider />
          <MenuItem>
            <RouterLink className="szh-menu__item--anchor" to="/policies">
              <IconText icon={icons.gear} text="Manage Policies" />
            </RouterLink>
          </MenuItem>
        </SubMenu>
        {(canEditAnyReleaseGroups || canCreateReleaseGroups) && isProjects(category) && (
          <CreateReleaseGroupItem
            isAtOrOverLimit={shouldRestrictCreateReleaseGroup}
            createReleaseGroupUrl={createReleaseGroupUrl}
          />
        )}
        <MenuItem className="fossa-menu__item-danger" onClick={handleShowDeleteModal}>
          Delete
        </MenuItem>
      </Menu>
    </>
  );
};

export default ProjectActionsMenu;
